import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HiOutlineSearch, HiChevronDown, HiChevronUp } from "react-icons/hi";
import { MdClear } from "react-icons/md";
import Button from "../Utilities/Button";
import PaginationNew from "../Utilities/PaginationNew";
import Modal from "../Utilities/Modal";
import RightPanelLayout from "../Layout/RightPanelLayout";
import StatusBadge from "./StatusBadge";
import {
  fetchPersonalSubscriptions,
  fetchPersonalSubscriptionDetail,
  setFilter,
  setPage,
  resetDetail,
  selectPersonalSubscription,
} from "../../store/slice/manager/personalSubscription";
import styles from "./style/KeystoneManagement.module.scss";

const LIMIT = 10;

const STATUS_META = {
  pending: { tone: "off", label: "待付款" },
  active: { tone: "on", label: "訂閱中" },
  payment_failed: { tone: "pending", label: "扣款失敗" },
  canceled: { tone: "off", label: "已取消" },
  expired: { tone: "off", label: "已到期" },
};

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "全部狀態" },
  { value: "pending", label: "待付款" },
  { value: "active", label: "訂閱中" },
  { value: "payment_failed", label: "扣款失敗" },
  { value: "canceled", label: "已取消" },
  { value: "expired", label: "已到期" },
];

// ISO 或 datetime 字串 → yyyy-mm-dd
const formatYmd = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const raw = String(value);
  const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  return isoMatch ? isoMatch[1] : raw.split(" ")[0] || raw;
};

const splitDateTime = (value) => {
  if (!value) {
    return { date: "", time: "" };
  }
  const raw = String(value);
  const date = formatYmd(raw);
  if (raw.includes("T")) {
    const time =
      raw.split("T")[1]?.replace(/\.\d+Z?$/, "").replace(/Z$/, "") ?? "";
    return { date, time };
  }
  const [, time = ""] = raw.split(" ");
  return { date, time };
};

const show = (value) =>
  value === null || value === undefined || value === "" ? "—" : value;

const showDate = (value) => {
  const ymd = formatYmd(value);
  return ymd ? ymd : "—";
};

const formatMoney = (n) => `NT$${(Number(n) || 0).toLocaleString("en-US")}`;

const pickUserName = (row) => row?.user?.name ?? "";
const pickUserEmail = (row) =>
  row?.user?.email ?? row?.buyer_email ?? "";

const billableItems = (sub) => {
  const items = Array.isArray(sub?.items) ? sub.items : [];
  return items.filter((line) => line?.status !== "canceled");
};

const monthlyTotalOf = (sub) =>
  billableItems(sub).reduce(
    (sum, line) => sum + (Number(line?.monthly_amount) || 0),
    0
  );

// 年扣制：實際入帳金額 = 單月費率（monthly_amount）× 12，沒有另外的年繳折扣欄位
const annualTotalOf = (sub) => monthlyTotalOf(sub) * 12;

const planName = (line) =>
  line?.plan?.name || `方案 #${line?.plan_id}`;

const PersonalSubscriptionList = () => {
  const dispatch = useDispatch();
  const { rows, total, page, loading, filter, detail, detailLoading } =
    useSelector(selectPersonalSubscription);

  const [draftSearch, setDraftSearch] = useState(filter.search || "");
  const [detailOpen, setDetailOpen] = useState(false);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / LIMIT)),
    [total]
  );

  const load = useCallback(() => {
    dispatch(fetchPersonalSubscriptions({ filter, page, limit: LIMIT }));
  }, [dispatch, filter, page]);

  useEffect(() => {
    load();
  }, [load]);

  const applySearch = () => {
    dispatch(setFilter({ search: draftSearch.trim() }));
  };

  const clearSearch = () => {
    setDraftSearch("");
    dispatch(setFilter({ search: "" }));
  };

  const sortDir = filter.sort_dir || "desc";
  const toggleSort = () => {
    dispatch(setFilter({ sort_dir: sortDir === "desc" ? "asc" : "desc" }));
  };

  const handlePageChange = (event) => {
    const next =
      event && typeof event === "object" && "selected" in event
        ? event.selected + 1
        : Number(event);
    if (Number.isInteger(next) && next > 0) {
      dispatch(setPage(next));
    }
  };

  const handleOpenDetail = (row) => {
    setDetailOpen(true);
    dispatch(fetchPersonalSubscriptionDetail(row.id));
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    dispatch(resetDetail());
  };

  const detailItems = billableItems(detail);
  const detailMonthly = monthlyTotalOf(detail);
  const detailAnnual = annualTotalOf(detail);
  const statusMeta = detail
    ? STATUS_META[detail.status] || STATUS_META.pending
    : STATUS_META.pending;

  return (
    <RightPanelLayout title="個人訂閱管理">
      <div className={styles.listToolbar}>
        <div className={styles.listToolbarTitle}>訂閱列表</div>
        <div className={styles.toolbarActions}>
          <select
            className={styles.select}
            value={filter.status}
            onChange={(e) => dispatch(setFilter({ status: e.target.value }))}
            aria-label="狀態篩選"
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <div className={styles.searchBar}>
            <button
              type="button"
              className={styles.searchBarIconBtn}
              onClick={applySearch}
              aria-label="搜尋"
            >
              <HiOutlineSearch className={styles.searchBarIcon} />
            </button>
            <input
              type="search"
              className={styles.searchBarInput}
              value={draftSearch}
              placeholder="搜尋姓名、Email 或統編"
              onChange={(e) => setDraftSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  applySearch();
                }
              }}
              aria-label="搜尋姓名、Email 或統編"
            />
            {draftSearch ? (
              <button
                type="button"
                className={styles.searchBarClear}
                onClick={clearSearch}
                aria-label="清除搜尋"
              >
                <MdClear className={styles.searchBarClearIcon} />
              </button>
            ) : null}
          </div>
          <Button type="primary" title="搜尋" onClick={applySearch} />
        </div>
      </div>

      {total > 0 ? (
        <div className={styles.paginationTop}>
          <PaginationNew
            onPageChange={handlePageChange}
            pageCount={page}
            limit={LIMIT}
            totalPages={totalPages}
            totalData={total}
          />
        </div>
      ) : null}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>
                <button
                  type="button"
                  className={styles.sortHeader}
                  onClick={toggleSort}
                  aria-label={
                    sortDir === "desc"
                      ? "送出時間：目前由新到舊，點擊改由舊到新"
                      : "送出時間：目前由舊到新，點擊改由新到舊"
                  }
                >
                  送出時間
                  {sortDir === "desc" ? (
                    <HiChevronDown className={styles.sortIcon} />
                  ) : (
                    <HiChevronUp className={styles.sortIcon} />
                  )}
                </button>
              </th>
              <th>姓名</th>
              <th>聯絡 Email</th>
              <th>年費合計</th>
              <th>訂閱狀態</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className={styles.tableEmptyCell} colSpan={6}>
                  載入中…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className={styles.tableEmptyCell} colSpan={6}>
                  尚無訂閱資料
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const { date, time } = splitDateTime(row.created_at);
                const meta = STATUS_META[row.status] || STATUS_META.pending;
                return (
                  <tr key={row.id}>
                    <td>
                      <div className={styles.cellStrong}>{date}</div>
                      <div className={styles.cellSub}>{time}</div>
                    </td>
                    <td className={styles.cellStrong}>
                      {show(pickUserName(row))}
                    </td>
                    <td>{show(pickUserEmail(row))}</td>
                    <td className={styles.cellStrong}>
                      {formatMoney(annualTotalOf(row))}
                    </td>
                    <td>
                      <StatusBadge tone={meta.tone} label={meta.label} />
                    </td>
                    <td>
                      <button
                        type="button"
                        className={styles.moreBtn}
                        onClick={() => handleOpenDetail(row)}
                      >
                        更多資訊
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={detailOpen}
        size="md"
        width="min(600px, calc(100vw - 32px))"
      >
        {detailOpen ? (
          <div className={styles.modalRoot}>
            <div className={styles.modalHead}>
              <div>
                <h3 className={styles.modalTitle}>
                  {show(pickUserName(detail))}
                </h3>
                <p className={styles.modalSub}>
                  送出時間 {showDate(detail?.created_at)}
                </p>
              </div>
              <button
                type="button"
                className={styles.modalClose}
                onClick={handleCloseDetail}
                aria-label="關閉"
              >
                ✕
              </button>
            </div>

            <div className={styles.modalBody}>
              {detailLoading && !detail ? (
                <div className={styles.tableEmptyCell}>載入中…</div>
              ) : (
                <>
                  <div className={styles.dividerLabel}>
                    帳號資料
                    <span className={styles.dividerHint}>
                      用於訂閱開通與帳務聯繫
                    </span>
                  </div>
                  <div className={styles.infoGrid}>
                    <div>
                      <div className={styles.infoKey}>姓名</div>
                      <div className={styles.infoVal}>
                        {show(pickUserName(detail))}
                      </div>
                    </div>
                    <div>
                      <div className={styles.infoKey}>聯絡 Email</div>
                      <div className={styles.infoVal}>
                        {show(pickUserEmail(detail))}
                      </div>
                    </div>
                    <div>
                      <div className={styles.infoKey}>統一編號</div>
                      <div className={styles.infoVal}>
                        {show(detail?.tax_id)}
                      </div>
                    </div>
                    <div>
                      <div className={styles.infoKey}>合約到期日</div>
                      <div className={styles.infoVal}>
                        {showDate(detail?.expiration_date)}
                      </div>
                    </div>
                    <div>
                      <div className={styles.infoKey}>下次扣款日</div>
                      <div className={styles.infoVal}>
                        {showDate(detail?.next_charge_date)}
                      </div>
                    </div>
                    <div>
                      <div className={styles.infoKey}>自動續約</div>
                      <div className={styles.infoVal}>
                        {detail?.auto_renew ? "是" : "否"}
                      </div>
                    </div>
                  </div>

                  <div className={styles.dividerLabel}>年費金額試算</div>
                  <div className={styles.breakdownBox}>
                    {detailItems.map((line) => (
                      <div key={`i-${line.id}`} className={styles.breakdownRow}>
                        <span className={styles.breakdownLabel}>
                          {planName(line)}
                        </span>
                        <span className={styles.breakdownValue}>
                          {formatMoney(line.monthly_amount)} / 月
                        </span>
                      </div>
                    ))}
                    {!detailItems.length ? (
                      <div className={styles.breakdownRow}>
                        <span className={styles.breakdownLabel}>
                          尚無方案明細
                        </span>
                        <span className={styles.breakdownValue}>—</span>
                      </div>
                    ) : null}
                    <div
                      className={`${styles.breakdownRow} ${styles.breakdownRowStrong}`}
                    >
                      <span className={styles.breakdownStrongText}>
                        合計
                      </span>
                      <span className={styles.breakdownStrongText}>
                        {formatMoney(detailMonthly)}
                      </span>
                    </div>
                    <div className={styles.grandTotalRow}>
                      <span>年總價（實際年扣金額）</span>
                      <span className={styles.grandTotalValue}>
                        {formatMoney(detailMonthly)} X 12 個月 = {formatMoney(detailAnnual)}
                      </span>
                    </div>
                  </div>

                  <div className={styles.contractNote}>
                    <b>合約規則：</b>
                    個人訂閱採年扣制、綁定一年；期間可加購服務（依距到期日剩餘月數比例計費，不滿一個月以一個月計算），不可減少。期滿依自動續約設定決定是否續約下一年整年金額。
                  </div>

                  <div className={styles.dividerLabel}>訂閱狀態</div>
                  <StatusBadge tone={statusMeta.tone} label={statusMeta.label} />
                </>
              )}
            </div>

            <div className={styles.modalFoot}>
              <button
                type="button"
                className={styles.btnGhost}
                onClick={handleCloseDetail}
              >
                關閉
              </button>
            </div>
          </div>
        ) : null}
      </Modal>
    </RightPanelLayout>
  );
};

export default PersonalSubscriptionList;
